import {useEffect, useMemo, useRef, useState} from "react";

import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {BorderArticle} from "@ui/general/BorderArticle";
import {useTranslation} from "react-i18next";
import {Scrollbar} from "react-typescript-scrollbar";

import styles from "./styles.module.scss";

const getOptionValue = (option) =>
  typeof option === "object" && option !== null
    ? (option.value ?? option.name)
    : option;

const getOptionLabel = (option) =>
  typeof option === "object" && option !== null
    ? (option.label ?? option.name)
    : option;

const resolveOptionLabel = (options, value) => {
  if (value == null || value === "") {
    return "";
  }

  const matchedOption = options.find(
    (option) => getOptionValue(option) === value,
  );

  return getOptionLabel(matchedOption ?? {name: value});
};

export const EditSelect = (props) => {
  const {
    width,
    activeOption,
    options,
    onSelect,
    onSearch,
    multiSelect,
    searchPlaceholder,
    placeholder = false,
    emptyResult,
    height,
    resetKey,
    resetOptionLabel,
  } = props;

  const {t} = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const isEmptyInitial =
    activeOption === "" || activeOption === undefined || activeOption === null;
  const [selectedOptions, setSelectedOptions] = useState(
    multiSelect
      ? Array.isArray(activeOption)
        ? activeOption
        : []
      : !isEmptyInitial
        ? [activeOption]
        : [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [term, setTerm] = useState(isEmptyInitial ? placeholder : false);

  const selectRef = useRef(null);
  const prevResetKeyRef = useRef(resetKey);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const isEmpty =
      activeOption === "" ||
      activeOption === undefined ||
      activeOption === null;

    setSelectedOptions(
      multiSelect
        ? Array.isArray(activeOption)
          ? activeOption
          : []
        : !isEmpty
          ? [activeOption]
          : [],
    );

    if (isEmpty) {
      setTerm(placeholder);
    } else {
      setTerm(false);
    }

    setSearchTerm("");
    setIsOpen(false);
  }, [activeOption, multiSelect, placeholder]);

  useEffect(() => {
    if (prevResetKeyRef.current !== resetKey) {
      const isEmpty =
        activeOption === "" ||
        activeOption === undefined ||
        activeOption === null;

      setSearchTerm("");
      setIsOpen(false);
      setSelectedOptions(
        multiSelect
          ? Array.isArray(activeOption)
            ? activeOption
            : []
          : !isEmpty
            ? [activeOption]
            : [],
      );

      if (isEmpty) {
        setTerm(placeholder);
      } else {
        setTerm(false);
      }

      prevResetKeyRef.current = resetKey;
    }
  }, [resetKey, activeOption, multiSelect, placeholder]);

  const prevOptionsRef = useRef(options);

  useEffect(() => {
    prevOptionsRef.current = options;
  }, [options]);

  const prevSearchTermRef = useRef("");

  useEffect(() => {
    if (
      onSearch &&
      searchTerm.trim() !== "" &&
      searchTerm.trim() !== prevSearchTermRef.current.trim()
    ) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        onSearch(searchTerm);
        prevSearchTermRef.current = searchTerm;
      }, 500);
    } else if (searchTerm.trim() === "") {
      const hadPreviousSearch = prevSearchTermRef.current.trim() !== "";
      prevSearchTermRef.current = "";
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (onSearch && hadPreviousSearch) {
        onSearch("");
      }
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, onSearch]);

  const toggleSelect = () => {
    setIsOpen(!isOpen);

    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleOptionClick = (option) => {
    if (option === null) {
      setSelectedOptions([]);
      setTerm(placeholder);
      onSelect(null);
      setIsOpen(false);
      return;
    }

    if (option?.isAction) {
      onSelect(option);
      setIsOpen(false);
      return;
    }

    if (multiSelect) {
      setSelectedOptions((prevSelected) => {
        const prevArray = Array.isArray(prevSelected) ? prevSelected : [];
        const optionValue = getOptionValue(option);

        if (prevArray.includes(optionValue)) {
          return prevArray.filter((item) => item !== optionValue);
        }

        return [...prevArray, optionValue];
      });
    } else {
      setSelectedOptions([getOptionValue(option)]);
    }

    onSelect(option);
    setIsOpen(false);
    setTerm(false);
  };

  const handleClickOutside = (event) => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const smartSearch = (text, searchTerm) => {
    if (!searchTerm || !text) return false;

    const normalizedText = text.toLowerCase().trim();
    const normalizedSearch = searchTerm.toLowerCase().trim();

    if (normalizedText === normalizedSearch) {
      return true;
    }

    if (normalizedText.includes(normalizedSearch)) {
      return true;
    }

    const words = text.split(/\s+/);
    const significantWords = words.filter((word) => word.length > 1);
    const uppercaseLetters = significantWords
      .map((word) => {
        const firstChar = word.charAt(0);

        return firstChar === firstChar.toUpperCase() &&
          firstChar !== firstChar.toLowerCase()
          ? firstChar.toLowerCase()
          : "";
      })
      .join("");

    if (uppercaseLetters && uppercaseLetters.includes(normalizedSearch)) {
      return true;
    }

    const allUppercaseLetters = text
      .split("")
      .filter(
        (char) => char === char.toUpperCase() && char !== char.toLowerCase(),
      )
      .join("")
      .toLowerCase();

    if (allUppercaseLetters && allUppercaseLetters.includes(normalizedSearch)) {
      return true;
    }

    let searchIndex = 0;
    for (
      let i = 0;
      i < normalizedText.length && searchIndex < normalizedSearch.length;
      i++
    ) {
      if (normalizedText[i] === normalizedSearch[searchIndex]) {
        searchIndex++;
      }
    }
    if (searchIndex === normalizedSearch.length) {
      return true;
    }

    return false;
  };

  const filteredOptions = useMemo(() => {
    if (!Array.isArray(options)) {
      return [];
    }
    if (searchTerm.trim() === "") {
      return options;
    }
    return options.filter((option) => {
      const optionText = [getOptionLabel(option), option.tooltip]
        .filter(Boolean)
        .join(" ");

      return smartSearch(optionText, searchTerm);
    });
  }, [options, searchTerm]);

  return (
    <div
      ref={selectRef}
      className={`${styles.select} ${isOpen ? styles.selectOpen : ""}`}
      data-select-open={isOpen ? "true" : undefined}
      style={{width: width ? `${width}rem` : "100%"}}
    >
      <div
        style={
          isOpen
            ? {borderBottom: "1rem solid var(--blue)"}
            : {borderBottom: "1rem solid var(--grey)"}
        }
        className={styles.selectHeader}
        onClick={toggleSelect}
      >
        <RootDesc>
          <span
            style={{
              color: term ? `#9ca3af` : `var(--black)`,
            }}
            dangerouslySetInnerHTML={{
              __html: multiSelect
                ? Array.isArray(selectedOptions)
                  ? selectedOptions.join(", ")
                  : ""
                : Array.isArray(selectedOptions) &&
                    selectedOptions.length > 0 &&
                    selectedOptions[0] != null &&
                    String(selectedOptions[0]).trim() !== ""
                  ? resolveOptionLabel(options, selectedOptions[0])
                  : term || "",
            }}
          />
        </RootDesc>

        <i
          style={
            isOpen ? {transform: "rotate(180deg)"} : {transform: "rotate(0deg)"}
          }
        />
      </div>

      {isOpen && (
        <div
          className={styles.selectList}
          style={{maxWidth: `500rem`}}
        >
          <BorderArticle>
            <div className={styles.selectBody}>
              {searchPlaceholder && (
                <input
                  type={"text"}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              )}

              <Scrollbar
                units={"rem"}
                contentHeight={height}
                contentPadding={10}
                barColor={"var(--greyLight)"}
                barBorderColor={
                  "linear-gradient(180deg, var(--border) 65%, transparent)"
                }
                barWidth={6}
                barRadius={8}
                thumbColor={
                  "linear-gradient(0deg, rgb(209, 139, 203) 0%, rgb(209, 186, 222) 50%, rgb(209, 139, 203) 100%)"
                }
                thumbWidth={6}
                thumbRadius={8}
                mask
                maskSize={95}
              >
                <ul>
                  {resetOptionLabel && (
                    <li
                      className={
                        Array.isArray(selectedOptions) &&
                        selectedOptions.length === 0
                          ? styles.activeOption
                          : ""
                      }
                      onClick={() => handleOptionClick(null)}
                    >
                      <SmallDesc>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: resetOptionLabel,
                          }}
                        />
                      </SmallDesc>
                    </li>
                  )}

                  {filteredOptions &&
                    filteredOptions.length > 0 &&
                    filteredOptions.map((option, index) => {
                      const optionValue = getOptionValue(option);

                      return (
                      <li
                        style={
                          !multiSelect &&
                          Array.isArray(selectedOptions) &&
                          selectedOptions.includes(optionValue)
                            ? {pointerEvents: "none", cursor: "default"}
                            : {}
                        }
                        className={[
                          Array.isArray(selectedOptions) &&
                          selectedOptions.includes(optionValue)
                            ? styles.activeOption
                            : "",
                          option.isAction ? styles.actionOption : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        key={option.id || optionValue || index}
                        title={option.tooltip || undefined}
                        onClick={() => handleOptionClick(option)}
                      >
                        <SmallDesc>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: getOptionLabel(option),
                            }}
                          />
                          {option.tooltip && (
                            <span
                              className={"mt-[4rem] block text-[var(--grey)]"}
                              dangerouslySetInnerHTML={{
                                __html: option.tooltip,
                              }}
                            />
                          )}
                        </SmallDesc>
                      </li>
                    );
                    })}

                  {filteredOptions && filteredOptions.length === 0 && (
                    <SmallDesc>
                      <span className={"text-[var(--grey)]"}>
                        {emptyResult || t("input.edit_select_empty")}
                      </span>
                    </SmallDesc>
                  )}
                </ul>
              </Scrollbar>
            </div>
          </BorderArticle>
        </div>
      )}
    </div>
  );
};

export default EditSelect;
